import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VisitorProvider } from './context/VisitorContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ModelForm from './components/ModelForm';
import PublicView from './components/PublicView';
import ModelDetail from './components/ModelDetail';
import ModelGallery from './components/ModelGallery';
import VisorRA from './components/VisorRA';
import GeoScenesAdmin from './components/geo/GeoScenesAdmin';
import GeoScenesPublic from './components/geo/GeoScenesPublic';
import GeoSceneViewer from './components/geo/GeoSceneViewer';
import Collection from './components/game/Collection';
import VisitorProfileGate from './components/game/VisitorProfileGate';
import EventsAdmin from './components/events/EventsAdmin';
import { db } from './firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';

const theme = createTheme({
  palette: {
    primary: {
      main: '#12234a',
      light: '#2d4a86',
      dark: '#0a1430',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#22d3ee',
      light: '#6de8ff',
      dark: '#0e9fb8',
      contrastText: '#03202a',
    },
    error: {
      main: '#ef4444',
    },
    success: {
      main: '#10b981',
    },
    background: {
      default: '#f4f6fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#0b1220',
      secondary: '#4b5563',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Sora", "Inter", sans-serif', fontWeight: 800 },
    h2: { fontFamily: '"Sora", "Inter", sans-serif', fontWeight: 800 },
    h3: { fontFamily: '"Sora", "Inter", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Sora", "Inter", sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"Sora", "Inter", sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"Sora", "Inter", sans-serif', fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        containedPrimary: {
          boxShadow: '0 8px 20px rgba(18, 35, 74, 0.25)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});

const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
};

const AdminLayout = ({ children }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <main style={{ flex: 1, padding: '24px 0' }}>
        {children}
      </main>
      <Footer />
    </div>
  );
};

const PublicLayout = ({ children }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <main style={{ flex: 1 }}>
        {children}
      </main>
      <Footer />
      <VisitorProfileGate />
    </div>
  );
};

const PublicARViewer = () => {
  const { modelId } = useParams();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModel = async () => {
      try {
        const docRef = doc(db, 'models', modelId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setModel({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error("Error getting document:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchModel();
  }, [modelId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#060b1c' }}>
        <CircularProgress size={56} sx={{ color: '#6de8ff' }} />
      </div>
    );
  }

  if (!model) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100vh', textAlign: 'center', padding: '40px', background: '#060b1c', color: '#e8f1ff',
        fontFamily: '"Inter", sans-serif'
      }}>
        <h2 style={{ fontFamily: '"Sora", sans-serif', margin: '0 0 8px' }}>Modelo no encontrado</h2>
        <p style={{ color: '#9fb4d6', margin: 0 }}>El modelo que buscas no existe o ha sido eliminado.</p>
      </div>
    );
  }

  return <VisorRA initialModelUrl={model.modelUrl} isMobile={window.innerWidth < 768} publicMode />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
        <VisitorProvider>
          <Routes>
            {/* Rutas públicas */}
            <Route path="/" element={
              <PublicLayout>
                <PublicView />
              </PublicLayout>
            } />
            
            <Route path="/model/:id" element={
              <PublicLayout>
                <ModelDetail />
              </PublicLayout>
            } />

            <Route path="/geoscenes" element={
              <PublicLayout>
                <GeoScenesPublic />
              </PublicLayout>
            } />

            <Route path="/geoscenes/:sceneId" element={
              <PublicLayout>
                <GeoSceneViewer />
              </PublicLayout>
            } />

            <Route path="/coleccion" element={
              <PublicLayout>
                <Collection />
              </PublicLayout>
            } />

            {/* Visor AR público */}
            <Route path="/viewer/:modelId" element={
              <PublicLayout>
                <PublicARViewer />
              </PublicLayout>
            } />
            
            {/* Login */}
            <Route path="/login" element={
              <>
                <Navbar hideLogin />
                <Login />
                <Footer />
              </>
            } />
            
            {/* Rutas protegidas (Admin) */}
            <Route path="/admin/*" element={
              <PrivateRoute>
                <AdminLayout>
                  <Routes>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="upload" element={<ModelForm />} />
                    <Route path="gallery" element={<ModelGallery />} />
                    <Route path="geoscenes" element={<GeoScenesAdmin />} />
                    <Route path="events" element={<EventsAdmin />} />
                    <Route path="edit/:modelId" element={<ModelForm editMode />} />
                    <Route path="view-ar/:modelId" element={<VisorRA />} />
                  </Routes>
                </AdminLayout>
              </PrivateRoute>
            } />
            
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </VisitorProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
